import { components } from "@apiSchema";
import { ID } from "@models/index";
import { prepareForMatching } from "@helpers";
import { routeNames } from "@core/routeNames";
import {
    iPermissionFields,
    PermissionLevelEnum,
    PermissionSection
} from "@models/PermissionSection";

interface iFoodProfileRecommendedChart {
    id: ID;
    title: string;
    typeLabel: string;
    link: string;
    downloadOptions?: Array<{
        title: string;
        url: string;
    }>
}

export interface iFoodProfileRecommendedCharts extends iPermissionFields {
    data: iFoodProfileRecommendedChart[];
}

type API_Model = components["schemas"]["MacAndMenuTrendDataViewModelChainPermissionWrapper"];

interface iData {
    apiModel?: API_Model;
    searchQuery: string;
}

export class FoodProfileRecommendedCharts implements iFoodProfileRecommendedCharts {
    static readonly fakeData: iFoodProfileRecommendedCharts['data'] = [
        {
            id: 1,
            title: "Fake Chart 1",
            typeLabel: "MAC",
            link: "https://www.google.com",
        },
        {
            id: 2,
            title: "Fake Chart 2",
            typeLabel: "Instant Chart",
            link: "https://www.google.com",
        },
        {
            id: 3,
            title: "Fake Chart 3",
            typeLabel: "MAC",
            link: "https://www.google.com",
        },
        {
            id: 4,
            title: "Fake Chart 4",
            typeLabel: "Instant Chart",
            link: "https://www.google.com",
        },
        {
            id: 5,
            title: "Fake Chart 5",
            typeLabel: "MAC",
            link: "https://www.google.com",
        },
        {
            id: 6,
            title: "Fake Chart 6",
            typeLabel: "Instant Chart",
            link: "https://www.google.com",
        },
        {
            id: 7,
            title: "Fake Chart 7",
            typeLabel: "MAC",
            link: "https://www.google.com",
        },
        {
            id: 8,
            title: "Fake Chart 8",
            typeLabel: "Instant Chart",
            link: "https://www.google.com",
        },
        {
            id: 9,
            title: "Fake Chart 9",
            typeLabel: "MAC",
            link: "https://www.google.com",
        },
        {
            id: 10,
            title: "Fake Chart 10",
            typeLabel: "Instant Chart",
            link: "https://www.google.com",
        },
        {
            id: 11,
            title: "Fake Chart 11",
            typeLabel: "MAC",
            link: "https://www.google.com",
        },
        {
            id: 12,
            title: "Fake Chart 12",
            typeLabel: "Instant Chart",
            link: "https://www.google.com",
        },
        {
            id: 13,
            title: "Fake Chart 13",
            typeLabel: "MAC",
            link: "https://www.google.com",
        },
        {
            id: 14,
            title: "Fake Chart 14",
            typeLabel: "Instant Chart",
            link: "https://www.google.com",
        },
        {
            id: 15,
            title: "Fake Chart 15",
            typeLabel: "MAC",
            link: "https://www.google.com",
        },
    ];

    hasData = true;
    permissionLevel = PermissionLevelEnum.None;
    data: iFoodProfileRecommendedChart[] = [];

    constructor(data?: iData) {
        if (data?.apiModel) {
            this.mapFromApi(data.apiModel, data.searchQuery);
        }
    }

    private setData(model: iFoodProfileRecommendedCharts
    ) {
        ({
            permissionLevel: this.permissionLevel,
            hasData: this.hasData,
            data: this.data,
        } = model);
    }

    private mapFromApi(rawData: API_Model, searchQuery: string) {
        if (rawData.data) {
            const macItems = rawData.data.mac?.items ?? [];
            const instantChartsItems = rawData.data.instantChart?.items ?? [];
            const categoryReports = rawData.data.categoryOverview?.items ?? [];

            const allItems: iFoodProfileRecommendedChart[] = [
                ...macItems.map(i => ({
                    id: i.id ?? 0,
                    title: i.name ?? '',
                    typeLabel: "MAC",
                    link: `${routeNames.viewDocument}?id=${i.id}&doctype=3&fromDocuments=${encodeURIComponent(searchQuery)}`,
                })),
                ...instantChartsItems.map(i => ({
                    id: i.id ?? 0,
                    title: i.name ?? '',
                    typeLabel: "Instant Chart",
                    link: `${routeNames.instantCharts}?q=${encodeURIComponent(searchQuery)}&id=${i.id}`,
                })),
                ...categoryReports.map(i => ({
                    id: i.id ?? 0,
                    title: i.name ?? '',
                    typeLabel: "Category Report",
                    link: '',
                    downloadOptions: [
                        { title: "PDF", url: i.pdfExportLink, },
                        { title: "PPT", url: i.pptExportLink, },
                    ],
                })),
            ];

            allItems.sort(({ title: a }, { title: b }) => {
                return prepareForMatching(a) < prepareForMatching(b) ? -1 : 1;
            });

            this.setData({
                permissionLevel: PermissionSection.getPermissionLevel(rawData.permissionLevel),
                hasData: rawData.hasData ?? allItems.length > 0,
                data: allItems,
            });
        }
    }
}